// src/components/SensorVisualization.jsx
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from 'recharts';

const SensorVisualization = ({ data, sensor, showForecast, timeRange }) => {
  const formatXAxis = (tickItem) => {
    if (timeRange === '1h') {
      const date = new Date(tickItem);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      const date = new Date(tickItem);
      return (
        date.toLocaleDateString() +
        ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    }
  };

  const getUnit = (sensorType) => {
    switch (sensorType) {
      case 'temperature':
        return '°C';
      case 'relative_humidity':
        return '%';
      case 'pressure':
        return 'hPa';
      default:
        return '';
    }
  };

  const getYAxisDomain = (sensorType, data) => {
    // Filter out null values and get min/max for both actual and forecast data
    const values = data
      .map((item) => [item[sensorType], item[`${sensorType}_forecast`]])
      .flat()
      .filter((val) => val !== null && val !== undefined);

    if (values.length === 0) return [0, 100];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1; // Add 10% padding

    return [min - padding, max + padding];
  };

  // Filter out any null or undefined forecast values
  const validData = data.filter((item) => {
    if (showForecast) {
      return item[sensor.value] !== null || item[`${sensor.value}_forecast`] !== null;
    }
    return item[sensor.value] !== null;
  });

  return (
    <div className="mt-6">
      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={validData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="timestamp"
              tickFormatter={formatXAxis}
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 12 }}
              interval="preserveStart"
            >
              <Label
                value="Time"
                position="bottom"
                offset={60}
                style={{ textAnchor: 'middle', fontSize: '14px', fill: '#666' }}
              />
            </XAxis>
            <YAxis domain={getYAxisDomain(sensor.value, validData)} tickFormatter={(value) => value.toFixed(1)}>
              <Label
                value={`${sensor.label} (${getUnit(sensor.value)})`}
                position="left"
                angle={-90}
                offset={-35}
                style={{ textAnchor: 'middle', fontSize: '14px', fill: '#666' }}
              />
            </YAxis>
            <Tooltip
              labelFormatter={(label) => new Date(label).toLocaleString()}
              formatter={(value, name) => {
                const isForecast = name.includes('Forecast');
                return [
                  value !== null && value !== undefined
                    ? `${value.toFixed(2)} ${getUnit(sensor.value)}`
                    : 'N/A',
                  isForecast ? `${sensor.label} Forecast` : sensor.label,
                ];
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '10px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              wrapperStyle={{
                paddingTop: '20px',
                fontSize: '14px',
                bottom: '0px',
                backgroundColor: 'white',
                borderTop: '1px solid #f0f0f0',
                width: '100%',
              }}
              offset={20}
            />

            {/* Historical data line */}
            <Line
              type="monotone"
              dataKey={sensor.value}
              stroke={sensor.color}
              name={sensor.label}
              dot={false}
              activeDot={{ r: 8 }}
              isAnimationActive={false}
              strokeWidth={2}
              connectNulls={true}
            />

            {/* Forecast data line */}
            {showForecast && (
              <Line
                type="monotone"
                dataKey={`${sensor.value}_forecast`}
                stroke={sensor.color}
                strokeDasharray="5 5"
                name={`${sensor.label} Forecast`}
                dot={false}
                activeDot={{ r: 8 }}
                isAnimationActive={false}
                strokeWidth={2}
                connectNulls={true}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SensorVisualization;
